import React, { Suspense, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useScroll, useMotionValueEvent } from "framer-motion";
import { ArrowRight } from "lucide-react";

import { PageSeo } from "@/components/seo/PageSeo";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { MaskReveal } from "@/components/motion/MaskReveal";
import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { usePrefillArea } from "@/hooks/use-contact-prefill";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

const SceneD = React.lazy(() => import("@/components/scenes/SceneD"));

function useMotionValueState(mv, initial = 0) {
  const [value, setValue] = useState(initial);
  useMotionValueEvent(mv, "change", (v) => setValue(v));
  return value;
}

function SceneFallback() {
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, background: "var(--surface-base)" }} />
  );
}

function Hero() {
  const { t } = useT("platformAi");
  return (
    <section
      className="container-frac pt-24 pb-16 md:pt-32 md:pb-24"
      data-testid="ai-hero"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow" data-testid="page-eyebrow">
            {t("hero.eyebrow")}
          </span>
        </Reveal>
        <MaskReveal
          as="h1"
          className="mt-6 text-4xl font-bold leading-tight tracking-tight text-ink-primary sm:text-5xl lg:text-6xl"
          data-testid="page-h1"
        >
          {t("hero.h1")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("hero.body")}
          </p>
        </Reveal>

        <Reveal delay={300}>
          <div
            className="mt-10 border-l-2 border-frac-violet/60 pl-5"
            data-testid="ai-si-substrate"
          >
            <span className="eyebrow">{t("siSubstrate.eyebrow")}</span>
            <p className="mt-3 text-lg leading-normal text-ink-secondary">
              {t("siSubstrate.paragraph")}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CategoriseSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const progress = useMotionValueState(scrollYProgress, 0);
  const { t } = useT("platformAi");

  return (
    <section
      ref={ref}
      className="relative"
      data-testid="ai-categorise"
    >
      <div className="relative min-h-[220vh]">
        <div className="sticky top-0 flex min-h-screen items-center py-16">
          <div className="container-frac w-full">
            <div className="mb-6 max-w-3xl">
              <Reveal>
                <span className="eyebrow">{t("categorise.eyebrow")}</span>
              </Reveal>
              <MaskReveal
                as="h2"
                className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
              >
                {t("categorise.h2")}
              </MaskReveal>
            </div>
            <div
              className="relative h-[65vh] w-full overflow-hidden rounded-[16px] border border-hairline"
              data-testid="ai-scene-d-wrapper"
            >
              <Suspense fallback={<SceneFallback />}>
                <SceneD
                  scrollProgress={progress}
                  style={{ position: "absolute", inset: 0 }}
                />
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pipeline() {
  const { t } = useT("platformAi");
  const steps = t("pipeline.steps", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="ai-pipeline"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("pipeline.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("pipeline.h2")}
        </MaskReveal>
      </div>

      <Stagger className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" gap={70}>
        {steps.map((s, i) => (
          <StaggerItem key={i}>
            <Card variant="glass" className="h-full" data-testid={`ai-step-${i + 1}`}>
              <div
                className="text-4xl font-light leading-none text-frac-violet"
                style={{ fontWeight: 300 }}
              >{`0${i + 1}`}</div>
              <h3 className="mt-3 text-lg font-bold text-ink-primary">
                {s.name}
              </h3>
              <p className="mt-2 text-sm leading-normal text-ink-secondary">
                {s.body}
              </p>
            </Card>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function Taxonomy() {
  const { t } = useT("platformAi");
  const axes = t("taxonomy.axes", { returnObjects: true }) || [];
  const testIdKeys = ["sector", "channel", "narrative", "persona", "demand", "conversion"];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="ai-taxonomy"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("taxonomy.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("taxonomy.h2")}
        </MaskReveal>
      </div>

      <Stagger className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" gap={70}>
        {axes.map((tt, i) => (
          <StaggerItem key={i}>
            <div
              className="rounded-[16px] border border-hairline bg-surface-elevated/40 px-5 py-6"
              data-testid={`ai-axis-${testIdKeys[i] || i}`}
            >
              <div className="text-[25px] font-bold text-ink-primary">
                {tt.name}
              </div>
              <p className="mt-2 text-xs text-ink-tertiary">{tt.body}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function Guardrails() {
  const { t } = useT("platformAi");
  const items = t("guardrails.items", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="ai-guardrails"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("guardrails.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("guardrails.h2")}
        </MaskReveal>
      </div>

      <Stagger className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3" gap={70}>
        {items.map((g, i) => (
          <StaggerItem key={i}>
            <div
              className="relative h-full rounded-[16px] border border-hairline bg-surface-raised/40 p-6"
              data-testid={`ai-guardrail-${i + 1}`}
            >
              <div
                className="absolute inset-x-0 top-0 h-[2px] rounded-t-[16px]"
                style={{ background: "rgba(220, 60, 60, 0.5)" }}
                aria-hidden
              />
              <h3 className="text-base font-bold text-ink-primary">{g.name}</h3>
              <p className="mt-3 text-sm leading-normal text-ink-secondary">
                {g.body}
              </p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function FinalCta() {
  const { t } = useT("platformAi");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="ai-final-cta"
    >
      <div className="mx-auto max-w-3xl text-center">
        <MaskReveal
          as="h2"
          className="text-3xl font-bold leading-tight tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("finalCta.h2")}
        </MaskReveal>
        <Reveal delay={300}>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Button
              as={Link}
              to={addLocalePrefix("/contact", locale)}
              variant="primary-on-dark"
              size="lg"
              data-testid="ai-final-cta-primary"
            >
              {t("finalCta.primary")}
            </Button>
            <Button
              as={Link}
              to={addLocalePrefix("/platform/data", locale)}
              variant="secondary-on-dark"
              size="lg"
              data-testid="ai-final-cta-secondary"
            >
              {t("finalCta.secondary")}
              <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function PlatformAI() {
  usePrefillArea("ai_infrastructure");
  const { t } = useT("platformAi");

  return (
    <>
      <PageSeo
        title={t("seoTitle")}
        description={t("seoDescription")}
        path="/platform/ai"
        image="/og/platform-ai.png"
      />
      <div data-testid="page-platform-ai">
        <Hero />
        <CategoriseSection />
        <Pipeline />
        <Taxonomy />
        <Guardrails />
        <FinalCta />
      </div>
    </>
  );
}
